import * as React from 'react';

import { GpsPulse } from '@/components/atoms/motion/GpsPulse';
import { cn } from '@/components/atoms/primitives/utils';

export interface DiscoverMapLoadingProps {
  /** Optional class merged onto the outer container. */
  className?: string;
}

/**
 * `DiscoverMapLoading` — calm pre-paint shown while the map takeover chunk
 * (`DiscoverMapTakeover` + the Google Maps SDK) downloads via `next/dynamic`.
 *
 * The DS 1.4.0 motion vocabulary assigns the map surface the `GpsPulse`
 * atom ("locating you" — concentric Skyline rings over a glowing dot). The
 * fallback paints its OWN dark Loci-Indigo ground (a theme-invariant brand
 * navy, not the theme-following page background) so the white caption + teal
 * pulse stay legible regardless of the active light/dark theme — the chunk
 * isn't mounted yet, so there's no dark map canvas underneath to lean on.
 * A faint Skyline grid echoes the map-canvas atmosphere from the design
 * source (`docs/design-system/1.4.0/templates/_shared/discover-map.css`).
 *
 * Decorative-by-design: `GpsPulse` owns the single `role="status"` /
 * `aria-label`, so the visible caption is `aria-hidden` to avoid a duplicate
 * announcement.
 */
export function DiscoverMapLoading({ className }: DiscoverMapLoadingProps): React.ReactElement {
  return (
    <div
      data-testid="discover-map-takeover-loading"
      className={cn(
        'flex min-h-[60dvh] w-full flex-col items-center justify-center gap-4',
        // Theme-invariant brand navy so the teal pulse + white caption read
        // in both themes (the map chunk — and its own dark canvas — has not
        // mounted yet).
        'bg-loci-indigo',
        // Faint Skyline lattice — the map-canvas atmosphere from the DS source.
        'dv-map-lattice',
        className,
      )}
    >
      <GpsPulse size={28} label="Loading the map" />
      <p
        className="font-mono text-micro uppercase tracking-[0.16em] text-ds-accent"
        aria-hidden="true"
      >
        Loading the map
      </p>
    </div>
  );
}
